import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import {
  NumberValueField,
  UnitSelector,
} from '@features/proposals/ElementListRow/ElementListRow';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { IElement } from '@shared/models/project.model';
import { useCandidateConversionEditor } from './use-candidate-conversion-editor.hook';

type CandidateConversionFieldProps = {
  row: CandidateRow;
  importedElement?: IElement;
};

/**
 * Conversion rate editor (factor, unit, swap) for a CSV candidate row. Rendered
 * in the import-tab list header when a row is selected.
 */
export const CandidateConversionField: React.FC<
  CandidateConversionFieldProps
> = ({ row, importedElement }) => {
  const {
    unitFrom,
    displayUnit,
    displayedRate,
    placeholderRate,
    unitOptions,
    rateSwapped,
    isDiscarded,
    isEditingRate,
    rateUnitMenuAnchor,
    seededFactor,
    setIsEditingRate,
    setRateUnitMenuAnchor,
    setRateSwapped,
    toCanonical,
    persistRate,
  } = useCandidateConversionEditor({ row, importedElement });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,

          '& > .MuiIconButton-root': {
            opacity: 0,
            color: 'rgba(0,0,0,0.3)',
            transition: 'opacity 0.15s, color 0.15s',
          },

          '&:hover > .MuiIconButton-root': { opacity: 1 },
        }}
      >
        {!isDiscarded && (
          <Tooltip title="Swap units">
            <IconButton
              size="small"
              sx={{
                p: '2px',
                mr: '2px',
                '&:hover': { color: 'rgba(0,0,0,0.8)' },
              }}
              onClick={() => setRateSwapped(!rateSwapped)}
            >
              <Icon category="mui" icon="SwapHoriz" size={16} />
            </IconButton>
          </Tooltip>
        )}
        <NumberValueField
          value={displayedRate !== undefined ? String(displayedRate) : ''}
          placeholder={
            placeholderRate !== undefined ? String(placeholderRate) : undefined
          }
          isEditing={isDiscarded ? false : isEditingRate}
          showHover={!isDiscarded}
          onSave={(value) => {
            if (isDiscarded) return;
            setIsEditingRate(false);
            persistRate(toCanonical(value));
          }}
          onStartEditing={() => {
            if (!isDiscarded) setIsEditingRate(true);
          }}
          onStopEditing={() => setIsEditingRate(false)}
        />
        {rateSwapped ? (
          <>
            {unitFrom && (
              <Typography
                noWrap
                sx={{
                  fontSize: 11,
                  letterSpacing: '0.55px',
                  color: 'rgba(0,0,0,0.5)',
                  pl: '2px',
                  pr: '4px',
                  flexShrink: 0,
                }}
              >
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {unitFrom}
                </Box>{' '}
                per
              </Typography>
            )}
            <UnitSelector
              unit={displayUnit ?? ''}
              color="rgba(0,0,0,0.8)"
              fontWeight={500}
              onClick={
                !isDiscarded && unitOptions.length > 1
                  ? (e) => setRateUnitMenuAnchor(e.currentTarget)
                  : undefined
              }
            />
          </>
        ) : (
          <>
            <UnitSelector
              unit={displayUnit ?? ''}
              color="rgba(0,0,0,0.8)"
              fontWeight={500}
              onClick={
                !isDiscarded && unitOptions.length > 1
                  ? (e) => setRateUnitMenuAnchor(e.currentTarget)
                  : undefined
              }
            />
            {unitFrom && (
              <Typography
                noWrap
                sx={{
                  fontSize: 11,
                  letterSpacing: '0.55px',
                  color: 'rgba(0,0,0,0.5)',
                  pl: '4px',
                  flexShrink: 0,
                }}
              >
                per{' '}
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {unitFrom}
                </Box>
              </Typography>
            )}
          </>
        )}
      </Box>
      <Menu
        open={rateUnitMenuAnchor !== null}
        anchorEl={rateUnitMenuAnchor}
        onClose={() => setRateUnitMenuAnchor(null)}
      >
        {unitOptions.map((unit) => (
          <Menu.Item
            key={unit}
            selected={unit === displayUnit}
            onClick={() => {
              setRateUnitMenuAnchor(null);
              persistRate(seededFactor, unit as ConversionFactorUnit);
            }}
          >
            {unit}
          </Menu.Item>
        ))}
      </Menu>
    </>
  );
};
